import { prayerEditorAuth, prayerEditorIsCurrent } from '@/lib/prayer-authoring';
import { useEffect } from 'react';
import { useAdiaStore } from '@/lib/store';
import { ParseError, parsePrayer } from '@/lib/grok';
import { summaryFromCorePrayer } from '@/lib/prayer-utils';
import {
  buildPrivacySafeEnrichmentContext,
  MAX_AUTOMATIC_ENRICHMENTS_PER_PASS,
} from '@/lib/privacy-safe-enrichment';
import { hasSyncSettled, hasCloudAccount } from '@/lib/sync-events';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { useReconnectRetry } from './useReconnectRetry';
import type { Prayer } from '@/types/prayer';
import { prayerContentMatches, prayerContentSnapshot, prayerOutboundExpectation } from '@/lib/outbound-prayer-authorization';

// Existing wake sources may overlap while a provider call is pending. Suppress
// duplicate calls only for that active owner/epoch/entry; no retry is scheduled.
const inFlight = new Set<string>();

// A prayer is enrichable when it has no parse yet AND is ready to parse:
// not an in-flight transcription (V.1.1 inline-save pending prayers carry
// rawText: '' + pendingTranscriptionId until transcription lands) and not
// blank. Parsing empty text would mislabel the prayer.
function isEnrichable(p: Prayer): boolean {
  return (
    (
      p.parsed === null ||
      (p.userEditedFields?.who === true && p.parsed.core_prayer === '')
    ) &&
    p.pendingEnrichment == null &&
    !p.pendingTranscriptionId &&
    p.rawText.trim() !== ''
  );
}

/**
 * App-wide prayer enrichment. Lifted out of PrayPage so a prayer created
 * from ANY surface (home compose, the Bible Pray-mode sheet) gets its
 * parse + gardenSummary regardless of the current route. Mounted EXACTLY
 * once (App.tsx's PrayerEnrichmentMount); it is the SOLE fresh-save +
 * recovery parser, so the prior parse/enrich race (commit 08693fb) stays
 * closed by construction as long as there is one mount.
 *
 * Keyed on authored input and time so a recovered transcription wakes the
 * existing parser without changing the prayer's recorded timestamp. Derived
 * metadata alone does not introduce another provider request.
 */
export function usePrayerEnrichment(): void {
  const signature = useAdiaStore((s) =>
    s.prayers
      .filter(isEnrichable)
      .map((p) => JSON.stringify([p.id, p.rawText, p.updatedAt]))
      .join(','),
  );

  // Reconnect / boot-settle retry via the shared helper (offline-first
  // Chunk 2): an offline parse fails and leaves the prayer untouched, so
  // `signature` (id:updatedAt) never changes and the effect below would never
  // re-run on its own. The helper bumps `tick` on online / visibility /
  // sync-settled / account-known, so a deferred or offline-failed row is
  // re-attempted on the next reconnect or once account state resolves. The
  // loop is idempotent and the per-device rule below gates correctness, so a
  // broad re-fire is safe.
  const { tick: retryTick } = useReconnectRetry();

  useEffect(() => {
    // Core-content cutover: the initial-parse worker mutates prayers and
    // sends prayer text to Grok. Both stay off while read-only is active; an
    // unparsed prayer simply stays unparsed and enriches later.
    if (isCoreReadOnly()) return;
    const auth = prayerEditorAuth();
    const all = useAdiaStore.getState().prayers;
    const targets = all
      .filter(isEnrichable)
      .slice(0, MAX_AUTOMATIC_ENRICHMENTS_PER_PASS);
    if (targets.length === 0) return;
    let cancelled = false;

    (async () => {
      for (const p of targets) {
        if (cancelled || !prayerEditorIsCurrent(auth)) break;
        // Per-device gate (§3.4): a has-account device with no settled
        // signed-in pull defers — bumping updatedAt could clobber a newer
        // cloud row before the boot pull arbitrates. A never-account device
        // passes (no cloud counterpart can exist). Re-checked per iteration so
        // a later target enriches if the latch opens mid-loop.
        if (!hasSyncSettled() && hasCloudAccount()) continue;
        const flightKey = JSON.stringify([auth.ownerId, auth.authEpoch, p.id]);
        if (inFlight.has(flightKey)) continue;
        inFlight.add(flightKey);
        try {
          const sourceText = p.rawText;
          const sourceSnapshot = prayerContentSnapshot(p);
          // Exclude this prayer from its own parse context, else it is
          // scored against itself and inherits misleading prior context
          // (rule #53).
          const { relatedContext, existingPeople, contextExpectations } =
            buildPrivacySafeEnrichmentContext(sourceText, all, p.id);
          const parsed = await parsePrayer(
            sourceText,
            relatedContext || undefined,
            existingPeople,
            {
              target: prayerOutboundExpectation(p, false),
              context: contextExpectations,
            },
          );
          // Cleanup stops starting more work, not an already-sent valid result.
          // Owner, exact input, eligibility and settle guards still apply below.
          if (!prayerEditorIsCurrent(auth)) break;
          // The lifecycle CAS checks exact input and current eligibility on
          // its authoritative snapshot, then revalidates at commit against
          // concurrent pulls. Preserve every recorded date on completion.
          await useAdiaStore.getState().applyEnrichment(
            p.id,
            (row) =>
              prayerEditorIsCurrent(auth) &&
              isEnrichable(row) &&
              row.rawText === sourceText &&
              prayerContentMatches(sourceSnapshot, row) &&
              (hasSyncSettled() || !hasCloudAccount()),
            (row) => ({
              ...row,
              parsed:
                row.userEditedFields?.who && row.parsed
                  ? {
                      ...parsed,
                      who: row.parsed.who,
                      mentionedPeople: row.parsed.mentionedPeople,
                    }
                  : parsed,
              gardenSummary: row.gardenSummary?.trim()
                ? row.gardenSummary
                : summaryFromCorePrayer(parsed.core_prayer),
            }),
          );
        } catch (err) {
          const status = err instanceof ParseError ? err.status : undefined;
          console.error(
            '[Adia] enrichment-failed',
            status ?? (err instanceof Error ? err.name : 'unknown'),
          );
          // A provider, admission, or network failure is normally systemic.
          // Stop this activation instead of spending the rest of a recovered
          // backlog into the same failure; reconnect or visibility retries it.
          if (status !== 400 && status !== 413) break;
        } finally {
          inFlight.delete(flightKey);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [signature, retryTick]);
}
